import React, { ReactNode, useContext, useEffect, useState } from 'react';

import SalesOrdersContext from '@totem/components/salesOrders/list/SalesOrdersContext';
import { SalesOrderLine } from '@totem/components/salesOrders/list/types';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import {
  CONTRACTS_ENDPOINT,
  SALES_ORDERS_ENDPOINT,
} from '@totem/utilities/endpoints';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

interface SalesOrderLineResults {
  lines: SalesOrderLine[];
  paging: {
    totalRecords: number;
  };
}

interface ServiceOption {
  id: string;
  name: string;
  displayName: string;
}

type FilterOption = { text: string; value: string };

let cachedServiceOptions: FilterOption[] | null = null;

type Props = {
  children?: ReactNode;
};

const SalesOrdersContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { regions, buildings } = useContext(OrganizationContext);
  const {
    buildParameters,
    setFilterOptions,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
    isReady,
  } = useContext(InputContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [lines, setLines] = useState<SalesOrderLine[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [refreshData, setRefreshData] = useState<boolean>(true);

  useEffect(() => {
    const orgOptions = [...(userProfile.organizations ?? [])]
      .filter((org) => org.isActive)
      .sort((chkA, chkB) => sortStringAscending(chkA.name, chkB.name))
      .map((org) => ({ text: org.name, value: org.id }));

    const regionOptions = [...(regions ?? [])]
      .sort((chkA, chkB) => sortStringAscending(chkA.name, chkB.name))
      .map((rgn) => ({ text: rgn.name, value: rgn.id }));

    const buildingOptions = [...(buildings ?? [])]
      .sort((chkA, chkB) => sortStringAscending(chkA.name, chkB.name))
      .map((bld) => ({ text: bld.name, value: bld.id }));

    setFilterOptions('organization', orgOptions);

    setFilterOptions('region', regionOptions);

    setFilterOptions('building', buildingOptions);

    if (cachedServiceOptions !== null) {
      setFilterOptions('service', cachedServiceOptions);
      return;
    }

    fetch(`${CONTRACTS_ENDPOINT}/services`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: ServiceOption[]) => {
        cachedServiceOptions = [...(result ?? [])]
          .sort((chkA, chkB) =>
            sortStringAscending(
              chkA.displayName || chkA.name,
              chkB.displayName || chkB.name,
            ),
          )
          .map((svc) => ({ text: svc.displayName || svc.name, value: svc.id }));

        setFilterOptions('service', cachedServiceOptions);
      });
  }, [userProfile.organizations, regions, buildings]);

  useEffect(() => {
    if (isReady) {
      setRefreshData(true);
    }
  }, [
    isReady,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
    userProfile.organization.id,
  ]);

  useEffect(() => {
    if (!isReady || !refreshData) {
      return;
    }

    setLoading(true);

    const params: Params = buildParameters();

    fetch(`${SALES_ORDERS_ENDPOINT}`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(params),
    })
      .then((res) => res.json())
      .then((result: SalesOrderLineResults) => {
        setLines(result?.lines ?? []);

        setTotalRecords(result?.paging?.totalRecords ?? 0);
      })
      .then(() => {
        setLoading(false);
      });

    setRefreshData(false);
  }, [refreshData, isReady, getApiToken, userProfile.organization.id]);

  return (
    <SalesOrdersContext.Provider
      value={{
        loading,
        lines,
        totalRecords,
        refetch: () => setRefreshData(true),
      }}
    >
      {children}
    </SalesOrdersContext.Provider>
  );
};

export default SalesOrdersContainer;
